<script setup>
import { ref, computed, watch } from 'vue';
import { useUserSkills } from '../composables/useUserSkills';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  skillId: {
    type: String,
    default: '',
  },
  position: {
    type: Object,
    default: () => ({ x: 0, y: 0 }),
  },
});

const emit = defineEmits(['close', 'remove']);

// State
const skillData = ref(null);
const loading = ref(false);
const error = ref(null);
const popoverRef = ref(null);

const { getSkill } = useUserSkills();

// Constants
const POPOVER_WIDTH = 320;
const VIEWPORT_PADDING = 16;

// Watchers
watch(
  () => props.show,
  async (show) => {
    if (show && props.skillId) {
      fetchSkillData();
    } else if (!show) {
      skillData.value = null;
      error.value = null;
    }
  }
);

watch(
  () => props.skillId,
  (newId) => {
    if (props.show && newId) {
      fetchSkillData();
    }
  }
);

// Actions
const fetchSkillData = async () => {
  loading.value = true;
  error.value = null;
  try {
    const res = await getSkill(props.skillId);
    if (res && (res.id || res.data)) {
      skillData.value = res.data || res;
    } else {
      error.value = 'Failed to load skill data';
    }
  } catch (err) {
    console.error('Error fetching skill:', err);
    error.value = err.message || 'Error loading skill';
  } finally {
    loading.value = false;
  }
};

// Safe position calculation
const safePosition = computed(() => {
  const { x, y } = props.position;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;

  const halfWidth = POPOVER_WIDTH / 2;
  let safeX = x;

  if (x - halfWidth < VIEWPORT_PADDING) {
    safeX = halfWidth + VIEWPORT_PADDING;
  }
  if (x + halfWidth > viewportWidth - VIEWPORT_PADDING) {
    safeX = viewportWidth - halfWidth - VIEWPORT_PADDING;
  }

  const spaceBelow = viewportHeight - y - VIEWPORT_PADDING;
  // If space below is less than 300px and space above is more than space below, show above
  const showAbove = spaceBelow < 300 && y > spaceBelow;

  return { x: safeX, y, showAbove };
});

const popoverStyle = computed(() => {
  const { x, y, showAbove } = safePosition.value;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;

  const style = {
    left: `${x - POPOVER_WIDTH / 2}px`,
    width: `${POPOVER_WIDTH}px`,
  };

  if (showAbove) {
    style.bottom = `${viewportHeight - y + 8}px`;
  } else {
    style.top = `${y + 8}px`;
  }

  return style;
});
</script>

<template>
  <Teleport to="body">
    <!-- Transparent Backdrop for click-outside -->
    <div v-if="show" class="fixed inset-0 z-[99]" @click="emit('close')" />

    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-2"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="show"
        ref="popoverRef"
        class="skill-popover fixed z-[100] bg-white rounded-3xl border-[0.5px] border-black/15 shadow-[0_0_20px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col max-h-[80vh]"
        :style="popoverStyle"
        @click.stop
        @mousedown.stop
      >
        <!-- Loading State -->
        <div v-if="loading" class="p-4 flex flex-col items-center justify-center gap-3">
          <div class="w-6 h-6 border border-black/20 rounded-full animate-spin" />
          <div class="text-sm text-black/40">Loading skill details...</div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="p-4 flex flex-col items-center justify-center gap-3">
          <div class="text-xs text-red-500">{{ error }}</div>
          <button
            @click="fetchSkillData"
            class="text-[10px] font-medium text-black/50 hover:text-black/80 underline"
          >
            Retry
          </button>
        </div>

        <!-- Content -->
        <template v-else-if="skillData">
          <!-- Cover & Header -->
          <div class="relative w-full h-48 overflow-hidden">
            <img
              v-if="skillData.cover"
              :src="skillData.cover"
              class="w-full h-full object-contain"
              :alt="skillData.name"
            />
            <div v-else class="w-full h-full flex items-center justify-center bg-black/[0.03]">
              <svg
                class="w-8 h-8 text-black/20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>

            <!-- Skill Tags -->
            <div
              v-if="skillData.tags && skillData.tags.length"
              class="absolute top-3 left-3 flex flex-wrap gap-1 max-w-[90%]"
            >
              <div
                v-for="tag in skillData.tags.slice(0, 2)"
                :key="tag.id || tag.name"
                class="px-2 py-0.5 rounded-md bg-black/40 backdrop-blur-md text-[10px] font-medium text-white/90 border border-white/10"
              >
                {{ tag.name }}
              </div>
              <div
                v-if="skillData.tags.length > 2"
                class="px-2 py-0.5 rounded-md bg-black/40 backdrop-blur-md text-[10px] font-medium text-white/90 border border-white/10"
              >
                +{{ skillData.tags.length - 2 }}
              </div>
            </div>
          </div>

          <div class="p-4 overflow-y-auto flex-1 border-t border-black/5">
            <h3 class="text-sm font-bold text-black/80 mb-1">
              {{ skillData.name }}
            </h3>
            <p class="text-xs text-black/50 leading-relaxed whitespace-pre-wrap break-words">
              {{ skillData.description || 'No description provided.' }}
            </p>
          </div>
        </template>
      </div>
    </Transition>
  </Teleport>
</template>
